import React, { useMemo, useState, useEffect } from "react";
import ContactForm from "../base-components/ContactForm";

const MOCK_DB = {
    films: [
        { title: "Taxi Driver", director: "Martin Scorsese", year: 1976, genre: "Drama", rating: 8.3, revenue: 28.3 },
        { title: "Raging Bull", director: "Martin Scorsese", year: 1980, genre: "Biography", rating: 8.2, revenue: 23.4 },
        { title: "Goodfellas", director: "Martin Scorsese", year: 1990, genre: "Crime", rating: 8.7, revenue: 47.1 },
        { title: "The Departed", director: "Martin Scorsese", year: 2006, genre: "Crime", rating: 8.5, revenue: 291.5 },
        { title: "The Irishman", director: "Martin Scorsese", year: 2019, genre: "Crime", rating: 7.8, revenue: 8.0 },
        { title: "Pulp Fiction", director: "Quentin Tarantino", year: 1994, genre: "Crime", rating: 8.9, revenue: 213.9 },
        { title: "Inglourious Basterds", director: "Quentin Tarantino", year: 2009, genre: "Adventure", rating: 8.3, revenue: 321.5 },
        { title: "Interstellar", director: "Christopher Nolan", year: 2014, genre: "Sci-Fi", rating: 8.6, revenue: 681.7 },
        { title: "Oppenheimer", director: "Christopher Nolan", year: 2023, genre: "Biography", rating: 8.4, revenue: 976.0 },
        { title: "Tenet", director: "Christopher Nolan", year: 2020, genre: "Sci-Fi", rating: 7.3, revenue: 365.3 },
    ],
};

function classNames(...c) { return c.filter(Boolean).join(" "); }

function SectionTitle({ eyebrow, title, desc }) {
    return (
        <div className="text-center max-w-3xl mx-auto">
            {eyebrow && <p className="uppercase tracking-widest text-[#9CA3AF] text-sm mb-3">{eyebrow}</p>}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-light leading-tight">{title}</h2>
            {desc && <p className="mt-4 text-gray-400 text-md">{desc}</p>}
        </div>
    );
}

function GradientBlob() {
    return (
        <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
            <div
                className="absolute inset-0 opacity-[0.08]"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.2) 1px, transparent 1px)",
                    backgroundSize: "36px 36px",
                }}
            />
            <div className="absolute -top-[20%] left-[25%] w-[70vw] h-[70vw] blur-[110px] opacity-40 animate-pulse"
                 style={{ background: "radial-gradient(circle, rgba(116,87,255,0.35), transparent 60%)" }} />
            <div className="absolute -bottom-[25%] right-[5%] w-[55vw] h-[55vw] blur-[120px] opacity-40 animate-pulse"
                 style={{ background: "radial-gradient(circle, rgba(0,200,255,0.28), transparent 60%)" }} />
        </div>
    );
}

const BAD_WORDS = ["cazzo", "merda", "fuck", "shit", "idiot", "stronzo", "bastard", "vaffanculo"];
const PRONOUNS_SELF = /\b(io|mio|mia|miei|mie|me|my)\b/i;

const detectBadInput = (q) => {
    const lower = q.trim().toLowerCase();
    if (!lower) return { type: "empty" };
    if (BAD_WORDS.some((w) => lower.includes(w))) return { type: "offensive" };
    return null;
};

const parseFilters = (questionRaw) => {
    const q = questionRaw.trim().toLowerCase();

    let directorMatch = q.match(/(?:di|by|ha diretto|directed by)\s+([a-zà-ú.\s'-]+)/i)?.[1] || null;
    if (directorMatch) {
        directorMatch = directorMatch
            .replace(/\s+(dal|from|between)\b.*$/i, "")
            .replace(/\s+\d{4}.*$/i, "")
            .trim();
    }

    const genreMatch =
        q.match(/(?:genere|genre)\s+([a-zà-ú\s-]+)/i)?.[1]?.trim() || null;

    const yearRange =
        q.match(/dal\s+(\d{4})\s+(?:al|to)\s+(\d{4})/i) ||
        q.match(/between\s+(\d{4})\s+and\s+(\d{4})/i);

    const yearAfter =
        q.match(/(?:dopo|after)\s+(?:il\s+|the\s+)?(\d{4})/i)?.[1] || null;

    const yearBefore =
        q.match(/(?:prima del|before)\s+(?:il\s+|the\s+)?(\d{4})/i)?.[1] || null;

    const isCountIntent = /(quanti|quante|how many|count)/i.test(q);

    return {
        director: directorMatch,
        genre: genreMatch,
        hasAnyTime: !!(yearRange || yearAfter || yearBefore),
        isCountIntent,
        hasAnyFilter: !!(directorMatch || genreMatch || yearRange || yearAfter || yearBefore),
        mentionsSelf: PRONOUNS_SELF.test(q),
    };
};

const NLtoSQL = (questionRaw) => {
    const q = questionRaw.trim().toLowerCase();

    let directorMatch = q.match(/(?:di|by|ha diretto|directed by)\s+([a-zà-ú.\s'-]+)/i);
    let director = directorMatch?.[1] || null;
    if (director) {
        director = director
            .replace(/\s+(dal|from|between)\b.*$/i, "")
            .replace(/\s+\d{4}.*$/i, "")
            .trim();
    }

    const yearRange =
        q.match(/dal\s+(\d{4})\s+(?:al|to)\s+(\d{4})/i) ||
        q.match(/between\s+(\d{4})\s+and\s+(\d{4})/i);

    const yearAfter = q.match(/(?:dopo|after)\s+(?:il\s+|the\s+)?(\d{4})/i);
    const yearBefore = q.match(/(?:prima del|before)\s+(?:il\s+|the\s+)?(\d{4})/i);
    const topN = q.match(/(?:top|migliori?|best)\s+(\d+)/i);
    const avgRating = /(?:media|average).*?(?:voti|rating)/i.test(q);
    const sumRevenue = /(incasso|revenue|box\s*office)/i.test(q);
    const list = /(elenca|lista|list|mostra|show)/i.test(q);
    const count = /(quanti|quante|how many|count)/i.test(q);
    const genreMatch = q.match(/(?:genere|genre)\s+([a-zà-ú\s-]+)/i);

    const where = [];

    if (director) {
        where.push(`director = '${director.replace(/'/g, "''")}'`);
    }

    if (genreMatch) {
        where.push(`genre ILIKE '%${genreMatch[1].trim().replace(/'/g, "''")}%'`);
    }

    if (yearRange) {
        where.push(`year BETWEEN ${yearRange[1]} AND ${yearRange[2]}`);
    }

    if (yearAfter) {
        where.push(`year > ${yearAfter[1]}`);
    }

    if (yearBefore) {
        where.push(`year < ${yearBefore[1]}`);
    }

    const WHERE = where.length ? `WHERE ${where.join(" AND ")}` : "";

    if (avgRating) {
        return `SELECT AVG(rating) AS avg_rating FROM films ${WHERE};`;
    }

    if (sumRevenue) {
        return `SELECT SUM(revenue) AS total_revenue FROM films ${WHERE};`;
    }

    if (topN) {
        return `SELECT title, director, year, rating FROM films ${WHERE} ORDER BY rating DESC LIMIT ${Number(
                topN[1]
        )};`;
    }

    if (count || /quanti film|how many movies|how many films/.test(q)) {
        return `SELECT COUNT(*) AS count FROM films ${WHERE};`;
    }

    if (list) {
        return `SELECT title, director, year, rating, genre FROM films ${WHERE} ORDER BY year ASC;`;
    }

    return `SELECT title, director, year, rating, genre FROM films ${WHERE} ORDER BY year DESC LIMIT 5;`;
};

const executePseudoSQL = (sql) => {
    const rows = [...MOCK_DB.films];

    let filtered = rows;
    const whereMatch = sql.match(/where\s+(.+?)\s+(order|limit|;|$)/i) || sql.match(/where\s+(.+?)\s*;$/i);
    if (whereMatch) {
        const clauses = whereMatch[1].split(/\s+AND\s+/i).map((c) => c.trim());

        filtered = rows.filter((r) => {
            return clauses.every((c) => {
                const dirEq = c.match(/^director\s*=\s*'(.*)'$/i);
                if (dirEq) return r.director.toLowerCase() === dirEq[1].toLowerCase();

                const genreIlike = c.match(/^genre\s+i?like\s+'%(.+)%'$/i);
                if (genreIlike) return r.genre.toLowerCase().includes(genreIlike[1].toLowerCase());

                const between = c.match(/^year\s+between\s+(\d{4})\s+and\s+(\d{4})$/i);
                if (between) return r.year >= Number(between[1]) && r.year <= Number(between[2]);

                const gt = c.match(/^year\s*>\s*(\d{4})$/i);
                if (gt) return r.year > Number(gt[1]);

                const lt = c.match(/^year\s*<\s*(\d{4})$/i);
                if (lt) return r.year < Number(lt[1]);

                return true;
            });
        });
    }

    if (/select\s+avg\(rating\)/i.test(sql)) {
        const avg = filtered.length === 0 ? null : filtered.reduce((s, r) => s + r.rating, 0) / filtered.length;
        return { type: "scalar", field: "avg_rating", value: avg !== null ? Number(avg.toFixed(2)) : null, rows: filtered };
    }
    if (/select\s+sum\(revenue\)/i.test(sql)) {
        const sum = filtered.reduce((s, r) => s + (r.revenue || 0), 0);
        return { type: "scalar", field: "total_revenue", value: Number(sum.toFixed(1)), rows: filtered };
    }
    if (/select\s+count\(\*\)/i.test(sql)) {
        return { type: "scalar", field: "count", value: filtered.length, rows: filtered };
    }

    const selColsMatch = sql.match(/select\s+(.+)\s+from\s+films/i);
    const cols = selColsMatch
        ? selColsMatch[1].split(",").map((c) => c.trim().split(/\s+as\s+/i)[0].trim())
        : ["title", "director", "year", "rating", "genre"];

    const orderMatch = sql.match(/order\s+by\s+([a-z_]+)\s+(asc|desc)/i);
    let out = [...filtered];
    if (orderMatch) {
        const key = orderMatch[1];
        const dir = orderMatch[2].toLowerCase();
        out.sort((a, b) => {
            const va = a[key];
            const vb = b[key];
            if (va < vb) return dir === "asc" ? -1 : 1;
            if (va > vb) return dir === "asc" ? 1 : -1;
            return 0;
        });
    }

    const limitMatch = sql.match(/limit\s+(\d+)/i);
    if (limitMatch) out = out.slice(0, Number(limitMatch[1]));

    const table = out.map((r) => {
        const obj = {};
        cols.forEach((c) => (obj[c] = r[c]));
        return obj;
    });

    return { type: "table", columns: cols, table, rows: filtered };
};

const formatAnswer = (question, sql, result) => {
    const director = sql.match(/director\s*=\s*'(.*)'/i)?.[1];
    const whereYears = sql.match(/year\s+between\s+(\d{4})\s+and\s+(\d{4})/i);
    const afterYear = sql.match(/year\s*>\s*(\d{4})/i)?.[1];
    const beforeYear = sql.match(/year\s*<\s*(\d{4})/i)?.[1];
    const scope = director
        ? ` per ${director}`
        : whereYears
            ? ` tra il ${whereYears[1]} e il ${whereYears[2]}`
            : afterYear
                ? ` dopo il ${afterYear}`
                : beforeYear
                    ? ` prima del ${beforeYear}`
                    : "";

    if (result.type === "scalar") {
        if (result.field === "count") return `Ho trovato ${result.value} film${scope}.`;
        if (result.field === "avg_rating") {
            if (result.value === null) return `Non ho trovato risultati${scope}.`;
            return `La valutazione media dei film${scope} è ${result.value}/10.`;
        }
        if (result.field === "total_revenue") return `L'incasso totale dei film${scope} è ${result.value} milioni di dollari.`;
    }

    if (result.type === "table") {
        if (result.table.length === 0) return `Non ho trovato risultati${scope}. Prova a riformulare.`;
        const short = result.table
            .slice(0, 5)
            .map((r) => `${r.title} (${r.year})${r.rating ? ` — rating ${r.rating}` : ""}`)
            .join("; ");
        const more = result.table.length > 5 ? ` (+${result.table.length - 5} altri)` : "";
        return `Ecco alcuni risultati${scope}: ${short}${more}.`;
    }

    return "Ecco il risultato della tua richiesta.";
};

function FeatureCard({ icon, title, desc }) {
    return (
        <div className="p-6 rounded-2xl bg-dark/40 border border-dark-100 shadow-lg hover:shadow-xl transition">
            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                <span className="text-[#A68BFF] text-2xl">{icon}</span>
            </div>
            <h3 className="text-xl text-light font-semibold">{title}</h3>
            <p className="text-gray-400 mt-2">{desc}</p>
        </div>
    );
}

export default function TwoSequel() {
    const [demoOpen, setDemoOpen] = useState(false);
    const [question, setQuestion] = useState("");
    const [sql, setSql] = useState("");
    const [answer, setAnswer] = useState("");
    const [table, setTable] = useState([]);
    const [columns, setColumns] = useState([]);
    const [thinking, setThinking] = useState(false);
    const [hintMode, setHintMode] = useState(null); // "offensive" | "nonsense" | "empty" | "unknown_director" | "needs_scope" | null
    const [shouldScroll, setShouldScroll] = useState(false);
    const [isContactFormOpen, setContactFormOpen] = useState(false);
    const examplePrompts = useMemo(
        () => [
            "Quanti film ha diretto Martin Scorsese?",
            "Elenca i film di Christopher Nolan dal 2010 al 2024",
            "Media rating dei film di Quentin Tarantino",
            "Top 3 film per rating del genere Crime",
            "Somma incasso dei film dopo il 2000",
        ],
        []
    );

    const openDemo = () => {
        setDemoOpen(true);
        setShouldScroll(true);
    };

    const runDemo = async () => {
        const bad = detectBadInput(question);
        const filters = parseFilters(question);
        const knownDirectors = new Set(MOCK_DB.films.map((f) => f.director.toLowerCase()));

        if (bad?.type === "empty") {
            setHintMode("empty");
            setSql("-- nessuna query generata: input vuoto --");
            setAnswer('Scrivi una domanda sulla demo dei film. Esempio: “Quanti film ha diretto Christopher Nolan?”');
            return;
        }
        if (bad?.type === "offensive") {
            setHintMode("offensive");
            setSql("-- bloccata per linguaggio improprio in questa demo --");
            setAnswer(
                "Manteniamo la demo utile e professionale. Prova con domande sui film, ad esempio: “Media rating dei film di Quentin Tarantino”."
            );
            return;
        }

        if (filters.mentionsSelf) {
            setHintMode("unknown_director");
            setSql("-- non disponibile: la demo usa un dataset di prova con alcuni registi noti --");
            setAnswer(
                "Questa demo è collegata a un piccolo dataset di prova e non include informazioni personali. Prova con un regista presente nel mock (es. “Quanti film ha diretto Martin Scorsese?”)."
            );
            return;
        }
        if (filters.director && !knownDirectors.has(filters.director.toLowerCase())) {
            setHintMode("unknown_director");
            setSql("-- regista non presente nel dataset di esempio --");
            setAnswer(
                `Nel dataset di esempio non trovo “${filters.director}”. La demo è limitata a un piccolo set di film.\nEsempi rapidi: “Quanti film ha diretto Christopher Nolan?”, “Top 3 film per rating del genere Crime”.`
            );
            return;
        }

        if ((filters.isCountIntent || /(?:elenca|lista|list|mostra|show)/i.test(question)) && !filters.hasAnyFilter) {
            setHintMode("needs_scope");
            setSql("-- richiesta troppo generica per la demo: aggiungi un filtro (regista, genere o periodo) --");
            setAnswer(
                "Per domande generiche la demo potrebbe risultare fuorviante. Aggiungi un filtro (regista, genere o periodo). Esempi: “Quanti film di Tarantino dopo il 2000?”, “Elenca i film di Nolan dal 2010 al 2024”."
            );
            return;
        }

        setThinking(true);
        setAnswer("");
        setSql("");
        setTable([]);
        setColumns([]);
        setHintMode(null);

        const sqlCandidate = NLtoSQL(question);
        const res = executePseudoSQL(sqlCandidate);
        const spoken = formatAnswer(question, sqlCandidate, res);

        setSql(sqlCandidate);
        setAnswer(spoken);
        if (res.type === "table") {
            setColumns(res.columns);
            setTable(res.table);
        }
        setThinking(false);
    };

    const onChooseExample = (p) => {
        setQuestion(p);
    };

    useEffect(() => {
        if (demoOpen && shouldScroll) {
            const el = document.getElementById("demo");
            if (el) {
                window.scrollTo({ top: el.offsetTop - 60, behavior: "smooth" });
            }
            setShouldScroll(false);
        }
    }, [demoOpen, shouldScroll]);

    useEffect(() => {
        if (isContactFormOpen) {
            scrollToContact()
        }
    }, [isContactFormOpen])

    const scrollToContact = () => {
        const el = document.getElementById("contact-form");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    const handleScroll = () => {
        if (isContactFormOpen) {
            scrollToContact()
        }
        else {
            setContactFormOpen(true);
        }
    }

    return (
        <main className="relative text-light overflow-x-hidden bg-[#0A0E14]">
            <GradientBlob/>

            <section className="pt-20 pb-16 sm:pt-24 sm:pb-20">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex flex-col items-center text-center gap-6">
                        <img
                            src="/2sequel_logo.webp"
                            alt="2Sequel"
                            className="w-[420px] sm:w-[520px] md:w-[640px] lg:w-[720px] max-w-[85vw] h-auto opacity-90 mx-auto select-none"
                            draggable={false}
                        />
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.1] break-words">
                            Interroga i tuoi dati in{" "}
                            <span
                                className="bg-clip-text text-transparent bg-gradient-to-r from-[#A78BFA] via-[#7C3AED] to-[#22D3EE]">
                                linguaggio naturale
                              </span>
                        </h1>
                        <p className="text-gray-400 max-w-3xl font-medium text-xl">
                            2Sequel trasforma domande in SQL, interroga il tuo database e risponde in modo chiaro:
                            numeri, liste o frasi
                            pronte da usare sulla tua interfaccia. Niente più sintassi da ricordare: parla e ottieni
                            risposte.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <button
                                onClick={handleScroll}
                                className="px-5 py-3 rounded-xl bg-[#7457FF] hover:bg-[#5F49D8] text-light font-medium shadow-lg shadow-[#7457FF]/20"
                            >
                                Get in touch
                            </button>
                            <button
                                onClick={openDemo}
                                className="px-5 py-3 rounded-xl bg-light text-dark font-medium hover:bg-light/90 border border-dark-100"
                            >
                                Prova la demo
                            </button>
                        </div>

                        <div className="w-full mt-8">
                            <div
                                className="relative mx-auto max-w-4xl rounded-3xl border border-dark-100 bg-white/5 backdrop-blur p-6 shadow-2xl">
                                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                                    <div className="flex-1 text-left">
                                        <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">Esempio</p>
                                        <p className="text-lg sm:text-xl">
                                            <span className="text-[#22D3EE]">Domanda:</span> Quanti film ha diretto
                                            Martin Scorsese?
                                        </p>
                                        <p className="text-lg sm:text-xl mt-1">
                                            <span className="text-[#10B981]">Risposta:</span> Ho trovato 5 film per
                                            Martin Scorsese.
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="text-xs text-gray-400">Vuoi provare?</div>
                                        <button
                                            onClick={() => {
                                                setQuestion("Quanti film ha diretto Martin Scorsese?");
                                                openDemo();
                                            }
                                            }
                                            className="px-4 py-2 rounded-lg bg-[#7457FF] text-light hover:bg-[#5F49D8]"
                                        >
                                            Apri demo
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <section className="py-12 sm:py-16">
                <div className="max-w-6xl mx-auto px-4">
                    <SectionTitle
                        eyebrow="Perché 2Sequel"
                        title="Dal linguaggio naturale all'azione sui tuoi dati"
                        desc="Riduci i tempi di analisi, democratizza l’accesso ai dati e ottieni risposte affidabili, subito."
                    />

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                        <FeatureCard icon="🧠" title="Comprensione delle domande"
                                     desc="Capisce intenti, filtri temporali, gruppi e metriche senza scrivere una riga di SQL."/>
                        <FeatureCard icon="🗄️" title="Compatibile con i tuoi DB"
                                     desc="Progettabile per Postgres, MySQL, SQL Server e data warehouse moderni."/>
                        <FeatureCard icon="🧾" title="Risposte spiegabili"
                                     desc="Mostra la query generata per audit e affidabilità."/>
                        <FeatureCard icon="⚡" title="Velocità operativa"
                                     desc="Riduce richieste al team data: i non tecnici trovano risposte in autonomia."/>
                        <FeatureCard icon="🔒" title="Sicurezza & policy"
                                     desc="Controlli su tabelle/campi consentiti, mascheramento dati e tracciabilità."/>
                        <FeatureCard icon="📈" title="Output adatto al contesto"
                                     desc="Contatori, medie, classifiche o liste: l’output si adatta al tipo di dato."/>
                    </div>

                    <div className="flex items-center justify-between gap-4 mb-6 mt-12">
                        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-100">Prova una demo su un DB di
                            film</h2>
                        <button
                            onClick={() => setDemoOpen((v) => !v)}
                            className={classNames(
                                "px-4 py-2 rounded-lg border",
                                demoOpen
                                    ? "bg-light text-dark border-dark-100"
                                    : "bg-white/5 text-gray-200 border-dark-100 hover:bg-white/10"
                            )}
                        >
                            {demoOpen ? "Nascondi demo" : "Apri demo"}
                        </button>
                    </div>

                    <div
                        id="demo"
                        className={classNames(
                            "transition-all duration-300",
                            demoOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
                        )}
                    >
                        <div className="rounded-2xl border border-dark-100 bg-white/5 p-6">
                            <div className="grid lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm text-gray-400">
                                        Scegli una domanda tra gli esempi
                                    </label>
                                    <textarea
                                        value={question}
                                        readOnly
                                        rows={4}
                                        placeholder="Seleziona una domanda tra gli esempi qui sotto"
                                        className="mt-2 w-full rounded-xl border border-dark-100 bg-dark/40 text-gray-100 placeholder:text-gray-500 p-3 focus:outline-none focus:ring-2 focus:ring-[#7457FF] cursor-default"
                                    />
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {examplePrompts.map((p) => (
                                            <button
                                                key={p}
                                                onClick={() => onChooseExample(p)}
                                                className="text-xs px-3 py-1 rounded-full bg-dark/60 border border-dark-100 text-gray-200 hover:bg-dark/80"
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-3 mt-4">
                                        <button
                                            onClick={runDemo}
                                            disabled={thinking || !question.trim()}
                                            className={classNames(
                                                "px-5 py-2 rounded-lg font-medium",
                                                thinking || !question.trim()
                                                    ? "bg-dark-100 text-gray-500 cursor-not-allowed"
                                                    : "bg-[#7457FF] text-light hover:bg-[#5F49D8]"
                                            )}
                                        >
                                            {thinking ? "Sto pensando…" : "Esegui"}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setQuestion("");
                                                setSql("");
                                                setAnswer("");
                                                setTable([]);
                                                setColumns([]);
                                                setHintMode(null);
                                            }}
                                            className="px-4 py-2 rounded-lg border border-dark-100 text-gray-300 hover:bg-dark/40"
                                        >
                                            Reset
                                        </button>
                                    </div>

                                    {hintMode && (
                                        <div className="mt-3 text-xs text-gray-400">
                                            {hintMode === "offensive" &&
                                                "Manteniamo la demo utile e professionale. Prova una domanda sul dominio dei film:"}
                                            {hintMode === "empty" &&
                                                "Hai bisogno di un'idea? Prova una delle seguenti domande:"}
                                            {hintMode === "unknown_director" &&
                                                "Questa demo usa un dataset di prova con alcuni registi noti. Ecco qualche spunto:"}
                                            {hintMode === "needs_scope" &&
                                                "Per evitare risposte fuorvianti nella demo, aggiungi un filtro (regista, genere o periodo):"}
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {examplePrompts.slice(0, 3).map((p) => (
                                                    <button
                                                        key={`hint-${p}`}
                                                        onClick={() => onChooseExample(p)}
                                                        className="text-[11px] px-2 py-1 rounded-full bg-white/5 border border-dark-100 text-gray-300 hover:bg-white/10"
                                                    >
                                                        {p}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="mt-2 italic text-gray-500">
                                                Demo semplificata: alcune richieste sono preimpostate per mostrarti il
                                                flusso NL → SQL → risposta.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div className="rounded-xl border border-dark-100 p-4 bg-dark/30">
                                        <p className="text-sm text-gray-400 mb-1">Query generata (pseudo-SQL)</p>
                                        <pre
                                            className="overflow-auto whitespace-pre-wrap break-words text-sm bg-dark/50 rounded-lg p-3 border border-dark-100 max-w-full text-gray-200">
                        {sql ? sql : "-- La query apparirà qui --"}
                    </pre>
                                    </div>

                                    <div className="rounded-xl border border-dark-100 p-4 bg-dark/30">
                                        <p className="text-sm text-gray-400 mb-1">Risposta</p>
                                        <div className="text-gray-100 break-words">{answer || "—"}</div>
                                    </div>

                                    {table.length > 0 && (
                                        <div className="rounded-xl border border-dark-100 p-4 bg-dark/30">
                                            <p className="text-sm text-gray-400 mb-3">Risultati</p>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full text-sm">
                                                    <thead>
                                                    <tr>
                                                        {columns.map((c) => (
                                                            <th
                                                                key={c}
                                                                className="text-left font-semibold px-3 py-2 border-b border-dark-100 text-gray-400"
                                                            >
                                                                {c}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {table.map((row, i) => (
                                                        <tr key={i} className="hover:bg-dark/40">
                                                            {columns.map((c) => (
                                                                <td
                                                                    key={c}
                                                                    className="px-3 py-2 border-b border-dark-100 break-words text-gray-200"
                                                                >
                                                                    {String(row[c])}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <p className="text-xs text-gray-500 mt-4">
                                Questa è una demo semplificata su dataset di prova. La versione completa si collega al
                                tuo schema reale,
                                applica controlli di sicurezza e fornisce spiegazioni dettagliate.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 sm:py-20">
                <div className="max-w-6xl mx-auto px-4">
                    <div
                        className="rounded-3xl border border-dark-100 bg-gradient-to-br from-[#0A0E14] via-[#121826] to-[#0A0E14] p-8 sm:p-10 text-center shadow-2xl">
                        <h3 className="text-2xl sm:text-3xl font-semibold">Porta il linguaggio naturale nel tuo
                            database</h3>
                        <p className="text-gray-400 mt-3">Scopri concretamente come l’AI può semplificare l’analisi dei
                            tuoi dati.</p>
                        <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
                            <button
                                onClick={handleScroll}
                                className="px-5 py-3 rounded-xl bg-[#7457FF] text-light font-medium hover:bg-[#5F49D8] shadow-lg shadow-[#7457FF]/20"
                            >
                                Get in touch
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            {isContactFormOpen && <ContactForm projectName={"2Sequel"}/>}
        </main>
    );
}
